import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { Modal, ModalProps } from "features";
import { Button, InputStyledDiv, Textarea, Loading } from "components";
import { convertDateFormat } from "helpers";
import { Comment, ClientComment, useCreateCommentMutation, useGetCommentsQuery } from "services";
import { t } from "i18next";

interface UserCommentsModalProps extends ModalProps {
  userId: string;
}

export const UserCommentsModal = ({ open, setOpen, userId }: UserCommentsModalProps) => {
  const [comment, setComment] = useState<string>("");
  const [disabled, setDisabled] = useState<boolean>(true);
  const [createComment, { isLoading: isCreatingComment }] = useCreateCommentMutation();
  const { data: comments, isLoading, isFetching, isError, refetch } = useGetCommentsQuery(userId);

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => setComment(event.target.value);

  const onSubmitHandler = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!comment) return;

    try {
      await createComment({ username: userId, comment }).unwrap();
      setComment("");
      await refetch(); // Refetch logs after creating a new one
    } catch (error) {
      console.error("Failed to create log entry:", error);
    }
  };

  useEffect(() => {
    setDisabled(!comment || isCreatingComment);
  }, [comment, isCreatingComment]);

  useEffect(() => {
    if (open) {
      setComment("");
      refetch(); // Fetch logs when modal opens
    }
  }, [open, refetch]);

  return (
    <div>
      <Modal withActions={false} withClose={true} open={open} setOpen={setOpen}>
        <div className="relative flex flex-col gap-y-6">
          <form onSubmit={onSubmitHandler}>
            <div className="flex flex-col gap-y-3">
              <h2 className="mb-3">{t("userManagement.addComment")}*</h2>
              <Textarea placeholder={t("userManagement.addYourComment")} value={comment} onChange={handleChange} />
            </div>
            <div className="mt-5 grid gap-3 sm:grid-flow-row-dense sm:grid-cols-2">
              <div className="flex items-center justify-center">
                <span className="cursor-pointer text-xs leading-6 text-secondary" onClick={() => setOpen(false)}>
                  {t("common.cancel")}
                </span>
              </div>
              <Button type="submit" disabled={disabled}>
                {t("common.save")}
              </Button>
            </div>
          </form>
          <div className="flex flex-col gap-y-3">
            {(isLoading || isFetching) && <Loading />}
            {isError && <div className="text-center text-sm text-red-500">{t("messages.loadError")}</div>}
            {!(isLoading || isFetching) && !isError && comments && (
              <>
                {comments.length === 0 && (
                  <p className="text-center text-sm text-gray-500">{t("userManagement.noLogsFound")}</p>
                )}
                {comments.length > 0 && (
                  <>
                    <h2 className="mb-3 text-blue-700">{t("userManagement.previousComments")}</h2>
                    <div className="flex flex-col">
                      {comments.flatMap((clientComment: ClientComment, index: number) =>
                        clientComment.comments.map((comment: Comment) => (
                          <div key={comment.id} className={`my-2 flex items-center justify-between px-4 py-2`}>
                            <span className="min-w-[120px] bg-blue-100 py-3 text-center text-sm font-medium text-gray-500 ">
                              {convertDateFormat(comment.created_at || "")}
                            </span>
                            <span className="ml-4 flex-1 bg-blue-100 py-3 text-center text-sm text-primary">
                              {comment.comment}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </>
                )}
              </>
            )}
            {isCreatingComment && <Loading absolute />}
          </div>
        </div>
      </Modal>
    </div>
  );
};
